import { BackgroundJobName, URLKeys } from 'noddi-async';
import type { FilterOptionsLoad, FilterSelectOption } from 'noddi-ui-web';

import { useFilterOptionsGet } from '../useFilterOptionsGet';

/** Known Celery broker queues (matches backend CELERY_QUEUE_* defaults). */
const KNOWN_QUEUES = ['default', 'sms', 'nsearch', 'integrations', 'payments', 'notifications', 'batch'] as const;

const CATALOG_TASK_NAMES = Object.values(BackgroundJobName);

const shortTaskLabel = (name: string) => {
  if (!name) {
    return name;
  }
  const parts = name.split('.');
  return parts.length > 2 ? parts.slice(-2).join('.') : name;
};

const mergeSelectedIntoOptions = (options: FilterSelectOption[], selected: string[]): FilterSelectOption[] => {
  if (!selected.length) {
    return options;
  }
  const existing = new Set(options.map((o) => String(o.value)));
  const missing = selected.filter((v) => !existing.has(v)).map((value) => ({ value, label: shortTaskLabel(value) }));
  return missing.length ? [...missing, ...options] : options;
};

const toCountOptions = (
  rows: { value: string; label: string; count: number }[] | undefined
): FilterSelectOption[] | undefined => {
  if (!rows?.length) {
    return undefined;
  }
  return rows.map((row) => ({
    value: row.value,
    label: row.label || row.value,
    count: row.count
  }));
};

/**
 * Loads facet counts + option lists for background-job multi-select filters.
 * Prefer filter-counts; fall back to filter-options / catalog when unavailable.
 */
export const useBackgroundJobFilterOptions = () => {
  const {
    data: counts,
    isLoading: isCountsLoading,
    isFetching: isCountsFetching,
    isError: isCountsError,
    refetch: refetchCounts
  } = useFilterOptionsGet({
    type: URLKeys.getBackgroundJobRunFilterCounts,
    queryConfig: {
      staleTime: 30_000
    }
  });

  const {
    data: options,
    isLoading: isOptionsLoading,
    isFetching: isOptionsFetching,
    isError: isOptionsError,
    refetch: refetchOptions
  } = useFilterOptionsGet({
    type: URLKeys.getBackgroundJobRunFilterOptions,
    queryConfig: {
      staleTime: 60_000,
      enabled: isCountsError
    }
  });

  const taskNameOptionsFromCounts = toCountOptions(
    counts?.taskNames?.map((row) => ({
      value: row.value,
      label: row.label || shortTaskLabel(row.value),
      count: row.count
    }))
  );
  const taskNameOptions: FilterSelectOption[] =
    taskNameOptionsFromCounts ??
    Array.from(new Set([...(options?.taskNames ?? []), ...CATALOG_TASK_NAMES]))
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: shortTaskLabel(value) }));

  const queueOptionsFromCounts = toCountOptions(counts?.queues);
  const queueOptions: FilterSelectOption[] =
    queueOptionsFromCounts ??
    Array.from(new Set([...(options?.queues ?? []), ...KNOWN_QUEUES]))
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));

  const workerOptionsFromCounts = toCountOptions(counts?.workers);
  const workerOptions: FilterSelectOption[] =
    workerOptionsFromCounts ?? (options?.workers ?? []).map((value) => ({ value, label: value }));

  /** What the sidebar shows beside the task, queue and worker options. */
  const load: FilterOptionsLoad = {
    isLoading: isCountsLoading || isCountsFetching || (isCountsError && (isOptionsLoading || isOptionsFetching)),
    // The fallback is usable when counts fail; only show failure when both sources fail.
    isError: isCountsError && isOptionsError,
    isFetching: isCountsFetching || isOptionsFetching,
    onRetry: () => {
      void refetchCounts();
      void refetchOptions();
    }
  };

  return {
    taskNameOptions,
    queueOptions,
    workerOptions,
    statusCounts: counts?.statuses,
    load,
    withSelected: mergeSelectedIntoOptions
  };
};
